import {
  Directive,
  HostListener,
  LOCALE_ID,
  inject
} from '@angular/core';

import type {
  ElementRef,
  Renderer2
} from '@angular/core';

@Directive()
export abstract class BaseNumberInputDirective {

  format: string;
  protected readonly groupSeparator: boolean;
  protected onTouch: () => void;
  protected onModelChange: (value: number) => void;
  protected focused: boolean = false;
  protected viewValue: string;
  protected value: number;
  private readonly localeId: string = inject(LOCALE_ID);
  private readonly decimalSeparator: string;

  protected constructor(protected groupSeparatorInput: 'true' | 'false' | null,
                        protected elementRef: ElementRef,
                        protected renderer: Renderer2) {
    this.groupSeparator = this.toBoolean(groupSeparatorInput);
    // Always operate as text input with decimal keypad hint
    this.renderer.setAttribute(this.elementRef.nativeElement, 'type', 'text');
    this.renderer.setAttribute(this.elementRef.nativeElement, 'inputmode', 'decimal');

    // Determine locale-specific decimal separator (default to '.')
    try {
      const parts = new Intl.NumberFormat(this.localeId).formatToParts(1.1);
      this.decimalSeparator = parts.find((p: Intl.NumberFormatPart) => p.type === 'decimal')?.value ?? '.';
    } catch {
      this.decimalSeparator = '.';
    }
  }

  /**
   * Update model on any input change (typing, paste, IME)
   */
  @HostListener('input', ['$event'])
  onInput(event: InputEvent | Event): void {
    const inputEl = event.target as HTMLInputElement | null;
    const raw = inputEl?.value ?? '';
    const sanitized = this.sanitizeToLocaleDecimal(raw);
    if (sanitized !== raw && inputEl) {
      this.renderer.setProperty(inputEl, 'value', sanitized);
    }

    let parsed: number;
    if (!this.isEmpty(sanitized)) {
      parsed = this.parseValue(sanitized);
      // If parsing failed (NaN), treat as undefined to avoid propagating NaN to the model
      if (isNaN(parsed as unknown as number)) {
        parsed = undefined as unknown as number;
      }
    }
    // Maintain previous semantics: when empty/unparseable, propagate undefined
    // (updateModel has a loose signature usage in existing codebase)
    this.updateModel(parsed as unknown as number);
    this.viewValue = sanitized;
  }

  // Backward compatibility: keep keyup handler for existing tests/subclasses
  @HostListener('keyup', ['$event'])
  onKeyUp(event: KeyboardEvent): void {
    const inputEl = event.target as HTMLInputElement | null;
    this.onInput({ target: inputEl } as unknown as InputEvent);
  }

  /**
   * Keyboard handling: keep Tab selection, block disallowed printable characters,
   * and avoid multiple decimal separators.
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const key = event.key;
    const isControl = this.isAllowedControlKey(event, key);
    const isSign = this.isAllowedSignKey(key);
    const isDigit = this.isAllowedDigitKey(key);
    const isDecimal = this.isAllowedDecimalKey(key);

    if (!(isControl || isSign || isDigit || isDecimal)) {
      event.preventDefault();
    }
  }

  private isAllowedControlKey(event: KeyboardEvent, key: string): boolean {
    // Allow control/meta shortcuts and all non-printable keys
    return event.ctrlKey || event.metaKey || key.length !== 1 || key === 'Tab' || key === 'Backspace' || key === 'Delete';
  }

  private isAllowedSignKey(key: string): boolean {
    if (key !== '+' && key !== '-') {
      return false;
    }
    const el = this.elementRef.nativeElement as HTMLInputElement;
    const current = el.value ?? '';
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const hasLeadingSign = current.startsWith('+') || current.startsWith('-');
    const replacingLeading = start === 0 && end > 0;
    return start === 0 && (!hasLeadingSign || replacingLeading);
  }

  private isAllowedDigitKey(key: string): boolean {
    return key >= '0' && key <= '9';
  }

  private isAllowedDecimalKey(key: string): boolean {
    if (key !== ',' && key !== '.') {
      return false;
    }
    const el = this.elementRef.nativeElement as HTMLInputElement;
    const current = el.value ?? '';
    const selectionStart = el.selectionStart ?? current.length;
    const selectionEnd = el.selectionEnd ?? current.length;
    const hasDecimal = current.includes(',') || current.includes('.');
    const selection = current.substring(selectionStart, selectionEnd);
    const replacingExisting = hasDecimal && selectionStart !== selectionEnd && (selection.includes(',') || selection.includes('.'));
    return !hasDecimal || replacingExisting;
  }

  /**
   * Sanitize pasted content to contain only digits and the locale decimal separator
   */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const data = event.clipboardData?.getData('text') ?? '';
    const el = this.elementRef.nativeElement as HTMLInputElement;
    const before = el.value ?? '';
    const start = el.selectionStart ?? before.length;
    const end = el.selectionEnd ?? before.length;
    const sanitized = this.sanitizeToLocaleDecimal(data);

    // Compose the new value respecting the current selection
    const next = `${before.substring(0, start)}${sanitized}${before.substring(end)}`;
    event.preventDefault();
    this.renderer.setProperty(el, 'value', next);
    // Trigger model update path
    this.onInput({ target: el } as unknown as InputEvent);
  }

  // touchstart handler removed: no more type toggling

  /**
   * For currency format with group separators view value will have to be rewritten
   * to remove group separators and currency characters
   */
  @HostListener('focus')
  onFocus(): void {
    this.focused = true;
    if (this.groupSeparator) {
      this.rewriteValue();
    }
  }

  /**
   * HostListener for blur event
   *
   * Rewrites view value when format changes depending on the focused state of the input
   * e.g. currency with group separators
   */
  @HostListener('blur')
  onBlur(): void {
    this.focused = false;
    this.rewriteValue();
    if (this.onTouch !== undefined) {
      this.onTouch();
    }
  }

  onChanges(): void {
    if (!this.isEmpty(this.value)) {
      this.rewriteValue(); // reformat input
    }
  }

  /**
   * see ControlValueAccessor.registerOnTouched
   *
   * @param onTouch
   */
  registerOnTouched(onTouch: () => void): void {
    this.onTouch = onTouch;
  }

  /**
   * see ControlValueAccessor.onModelChange
   *
   * @param onModelChange
   */
  registerOnChange(onModelChange: () => void): void {
    this.onModelChange = onModelChange;
  }

  /**
   * see ControlValueAccessor.writeValue
   *
   * Writes model's value to the input
   *
   * @param value
   */
  writeValue(value: unknown): void {
    let formattedValue = '';

    if (!this.isEmpty(value)) {
      formattedValue = this.formatValue(value as number);
    }
    if ((this.viewValue !== formattedValue || this.viewValue === undefined)) {
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', formattedValue);
      this.viewValue = formattedValue;
    }
    this.value = value as number;
  }

  protected rewriteValue(): void {
    if (!isNaN(this.value)) {
      this.writeValue(this.value);
    }
  }

  protected updateModel(value: number): void {
    if (this.value !== value) {
      if (this.onModelChange !== undefined) {
        this.onModelChange(value);
      }
      this.value = value;
    }
  }

  protected isEmpty(value: unknown): boolean {
    return  value === undefined || value === null || isNaN(value as number)
      || (typeof value === 'string' && value.trim() === '');
  }

  protected toBoolean(value: unknown): boolean {
    return value != null && `${value}` !== 'false';
  }

  private sanitizeToLocaleDecimal(text: string): string {
    if (!text) {
      return '';
    }
    // Preserve at most one leading sign and remove any other '+' or '-' characters
    let s = text;
    let sign = '';
    if (s[0] === '+' || s[0] === '-') {
      sign = s[0];
    }
    // Remove all sign characters then re-attach a single leading one if needed
    s = s.replace(/[+-]/g, '');
    // Keep only digits and decimal symbols after sign handling
    s = s.replace(/[^0-9.,]/g, '');
    // Normalize to the locale decimal separator: convert the non-locale separator into the locale one
    const sep = this.decimalSeparator;
    const other = sep === ',' ? '.' : ',';
    s = s.replace(new RegExp('\\' + other, 'g'), sep);
    // Allow only the first decimal separator (collapse duplicates)
    const first = s.indexOf(sep);
    if (first !== -1) {
      const head = s.substring(0, first + 1);
      const tail = s.substring(first + 1).replace(new RegExp('\\' + sep, 'g'), '');
      s = head + tail;
    }
    // Prepend the sign back only if present
    if (sign) {
      s = sign + s;
    }
    return s;
  }

  protected abstract formatValue(value: number): string;
  protected abstract parseValue(value: string): number;
}
